import React, { useState } from 'react';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { Alert, Link } from '@mui/material';
import { useTenantAppConfig } from 'op-utils/useTenantAppConfig';

export const dismissalStorageKey = (campaignId: string): string => `outage-banner-dismissed-${campaignId}`;

// Entry surfaces only: staff dashboards, search, mini-rego, and the UK
// eRegistration flow. Patient summary and the careplan workflow are
// EXCLUDED BY DESIGN (Jul 2026): those screens need the vertical real
// estate for clinical content, and their shells size themselves with
// hardcoded 100vh/calc(100vh - 3.5em) that clips when anything extra sits
// above the header (patient card header clips, workflow footer pushed
// off-screen). Do not widen this list without checking both of those
// constraints.
export const BANNER_ROUTE_PATTERNS: RegExp[] = [
  /^\/radiation$/,
  /^\/radiation\/careplans/, // careplans dashboard tab — same shell as appointments
  /^\/radiation\/registration\/patient\//, // RO mini-rego — same shell as the dashboards
  /^\/search$/,
  /^\/appointments$/, // eRegistration (UK PSO) dashboard
  /^\/patient$/, // eRegistration login
  /^\/registration\//, // eRegistration form sections
  /^\/patient\/[^/]+\/registration$/, // eRegistration entry for a patient
  /^\/patient\/registrationComplete$/,
];

const OutageBanner = (): JSX.Element | null => {
  const { pathname } = useLocation();
  const { showBanner, message, link, campaignId, startDate, endDate } = useTenantAppConfig().Outage;
  // Tenant overrides load async, so campaignId can change between renders;
  // derive dismissal from storage each render rather than a useState initializer
  const [dismissedCampaigns, setDismissedCampaigns] = useState<string[]>([]);
  const dismissed =
    dismissedCampaigns.includes(campaignId) ||
    (Boolean(campaignId) && localStorage.getItem(dismissalStorageKey(campaignId)) === 'true');

  const now = dayjs();
  const withinWindow =
    Boolean(startDate) && Boolean(endDate) && !now.isBefore(dayjs(startDate)) && now.isBefore(dayjs(endDate));

  const onBannerRoute = BANNER_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));
  if (!onBannerRoute || !showBanner || !withinWindow || dismissed) return null;

  const handleDismiss = (): void => {
    localStorage.setItem(dismissalStorageKey(campaignId), 'true');
    setDismissedCampaigns([...dismissedCampaigns, campaignId]);
  };

  return (
    <Alert
      severity="info"
      data-testid="outage-banner"
      onClose={handleDismiss}
      sx={{
        borderRadius: 0,
        justifyContent: 'center',
        '& .MuiAlert-message': { textAlign: 'center' },
      }}>
      {message}
      {link && (
        <Link
          component="a"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="outage-banner-link"
          sx={{ marginLeft: 1 }}>
          Learn more
        </Link>
      )}
    </Alert>
  );
};

export default OutageBanner;
